import * as React from "react";
import {
  createFileRoute,
  redirect,
  stripSearchParams,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  EyeIcon,
  MoreVertical,
  PencilIcon,
  PlusIcon,
  Search,
  Settings2Icon,
  TrashIcon,
} from "lucide-react";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { HTTPError } from "ky";
import { format } from "date-fns";
import type { UseFormReturn } from "react-hook-form";
import type { ColumnDef, Table as TableType } from "@tanstack/react-table";
import { asBillingError } from "@/lib/http/billing";
import { MeetingsPagination } from "@/routes/_app/meetings/-components/MeetingsPagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilterChip } from "@/components/ui/filter-chip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ConfirmationDialog,
  ConfirmationDialogWarningIcon,
  UnsavedChangesDialog,
} from "@/components/ui/confirmation-dialog";
import { api } from "@/lib/http";
import { passwordSchema } from "@/lib/auth";
import {
  generateDefaultValue,
  paginatedListSchema,
  requiredEmail,
  requiredString,
} from "@/lib/utils/zod";
import { PasswordInput } from "@/components/ui/passwordInput";
import { useDebouncedSearch } from "@/lib/hooks";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import { invalidateBillingSeatUsage } from "@/lib/queries/billing";
import { cn } from "@/lib/utils";
import {
  collapseFilters,
  isAllSelected,
  toggleOption,
} from "@/lib/utils/filter-options";

const DUPLICATE_EMAIL_ERROR = "User with this email already exists";

const userFormSchema = z.object({
  full_name: requiredString("Full name is required"),
  email: requiredEmail("Valid email is required"),
  password: passwordSchema,
  is_active: z.boolean(),
});

const userUpdateFormSchema = userFormSchema.extend({
  password: passwordSchema.optional(),
});

const searchSchema = z.object({
  page: z.number().int().positive().default(1).catch(1),
  per_page: z.number().int().positive().max(100).default(10),
  search: z.string().optional(),
  is_active: z.boolean().optional(),
});

const userSchema = userFormSchema.extend({
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});

const usersListSchema = paginatedListSchema(userSchema);

type UserFormData = z.infer<typeof userFormSchema>;
type UserUpdateFormData = z.infer<typeof userUpdateFormSchema>;
type User = z.infer<typeof userSchema>;
type UsersList = z.infer<typeof usersListSchema>;

type SearchParams = z.infer<typeof searchSchema>;

function getBackendFieldError(body: unknown, fieldName: string): string | null {
  const detail =
    body && typeof body === "object" && "detail" in body ? body.detail : body;

  if (!Array.isArray(detail)) return null;

  for (const issue of detail) {
    if (!issue || typeof issue !== "object") continue;

    const location = "loc" in issue ? issue.loc : null;
    const message = "msg" in issue ? issue.msg : null;

    if (
      Array.isArray(location) &&
      location.at(-1) === fieldName &&
      typeof message === "string"
    ) {
      return message;
    }
  }

  return null;
}

function getBackendDetailMessage(body: unknown): string | null {
  if (typeof body === "string") return body;
  if (!body || typeof body !== "object" || !("detail" in body)) return null;
  return typeof body.detail === "string" ? body.detail : null;
}

export const Route = createFileRoute("/_app/users/")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  search: {
    middlewares: [stripSearchParams({ search: "" })],
  },

  loaderDeps: ({ search }) => search,
  async beforeLoad({ context: { queryClient } }) {
    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);
    if (!userInfo.org_admin) throw redirect({ to: "/" });
  },
  loader({ context: { queryClient }, deps }) {
    queryClient.ensureQueryData(usersListQueryOptions(deps));
  },
});

function usersListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["users", "list", params],
    async queryFn({ client }) {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: params.per_page.toString(),
      });

      if (params.search) searchParams.set("search", params.search);
      if (params.is_active !== undefined)
        searchParams.set("is_active", params.is_active.toString());

      const users = await api.auth.raw
        .get("api/v1/users/", { searchParams })
        .json<UsersList>();

      users.items.map((user) =>
        client.setQueryData(["users", "detail", user.id], user),
      );

      return users;
    },
  });
}

const tableHeadClass =
  "px-4 py-3 text-left text-sm font-medium text-[#717680] dark:text-muted-foreground md:px-6";
const tableCellClass = "px-4 py-4 md:px-6";

const userMenuItemClassName = cn(
  "mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-[#414651] dark:text-foreground",
  "focus:bg-muted focus:text-[#414651] data-highlighted:bg-muted data-highlighted:text-[#414651] dark:focus:text-foreground dark:data-highlighted:text-foreground",
);

function UserActionsMenu({
  user,
  onView,
  onEdit,
  onDelete,
}: {
  user: User;
  onView: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-label="User actions"
        >
          <MoreVertical className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="w-[168px] rounded-lg border-border p-1 shadow-lg"
      >
        <DropdownMenuItem
          className={userMenuItemClassName}
          onClick={() => onView(user)}
        >
          <EyeIcon className="h-[18px] w-[18px] shrink-0 stroke-[1.75]" />
          View
        </DropdownMenuItem>
        <DropdownMenuItem
          className={userMenuItemClassName}
          onClick={() => onEdit(user)}
        >
          <PencilIcon className="h-[18px] w-[18px] shrink-0 stroke-[1.75]" />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          className={userMenuItemClassName}
          onClick={() => onDelete(user)}
        >
          <TrashIcon className="h-[18px] w-[18px] shrink-0 stroke-[1.75]" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const STATUS_OPTIONS = ["All", "Active", "Inactive"] as const;
type StatusOption = (typeof STATUS_OPTIONS)[number];
const INDIVIDUAL_STATUS_OPTIONS: Array<StatusOption> = ["Active", "Inactive"];

function statusesToIsActive(
  statuses: Array<StatusOption>,
): boolean | undefined {
  const collapsed = collapseFilters(INDIVIDUAL_STATUS_OPTIONS, statuses, "All");
  if (collapsed.length !== 1 || collapsed[0] === "All") return undefined;
  return collapsed[0] === "Active";
}

function isActiveToStatuses(
  isActive: boolean | undefined,
): Array<StatusOption> {
  if (isActive === undefined) return [...STATUS_OPTIONS];
  return [isActive ? "Active" : "Inactive"];
}

function UsersFilterPopover({
  isActive,
  onApply,
}: {
  isActive: boolean | undefined;
  onApply: (isActive: boolean | undefined) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<Array<StatusOption>>(
    isActiveToStatuses(isActive),
  );

  React.useEffect(() => {
    if (open) setDraft(isActiveToStatuses(isActive));
  }, [open, isActive]);

  const toggle = (status: StatusOption) => {
    setDraft((current) =>
      toggleOption(
        STATUS_OPTIONS,
        INDIVIDUAL_STATUS_OPTIONS,
        current,
        status,
        "All",
      ),
    );
  };

  const hasActiveFilters = isActive !== undefined;
  const isAllStatusesSelected = isAllSelected(INDIVIDUAL_STATUS_OPTIONS, draft);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn("h-10 w-10 shrink-0", hasActiveFilters)}
          aria-label="Filter users"
        >
          <Settings2Icon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-6">
        <h3 className="mb-6 text-lg font-semibold text-foreground">Filter</h3>

        <div>
          <p className="mb-3 text-base font-medium text-foreground">Status</p>
          <div className="flex flex-wrap gap-3">
            {STATUS_OPTIONS.map((status) => (
              <FilterChip
                key={status}
                label={status}
                selected={draft.includes(status)}
                onClick={() => toggle(status)}
              />
            ))}
          </div>
        </div>

        <div className="mt-7 flex justify-end gap-3">
          <Button
            variant="outline"
            disabled={isAllStatusesSelected}
            onClick={() => setDraft([...STATUS_OPTIONS])}
          >
            Reset Filter
          </Button>
          <Button
            onClick={() => {
              onApply(statusesToIsActive(draft));
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function buildColumns(
  onView: (user: User) => void,
  onEdit: (user: User) => void,
  onDelete: (user: User) => void,
): Array<ColumnDef<User>> {
  return [
    {
      accessorKey: "full_name",
      header: "Full Name",
      size: 230,
      meta: {
        cellClassName: cn(
          tableCellClass,
          "text-sm font-medium text-[#181D27] dark:text-foreground",
        ),
      },
      cell: ({ row }) => (
        <span className="block truncate">{row.original.full_name}</span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      size: 280,
      meta: {
        cellClassName: cn(
          tableCellClass,
          "text-sm text-[#535862] dark:text-muted-foreground",
        ),
      },
      cell: ({ row }) => (
        <span className="block truncate">{row.original.email}</span>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created at",
      size: 220,
      meta: { cellClassName: tableCellClass },
      cell: ({ row }) =>
        format(new Date(row.original.created_at), "MMM d, yyyy, h:mm:ss a"),
    },
    {
      accessorKey: "updated_at",
      header: "Updated at",
      size: 220,
      meta: { cellClassName: tableCellClass },
      cell: ({ row }) =>
        row.original.updated_at
          ? format(new Date(row.original.updated_at), "MMM d, yyyy, h:mm:ss a")
          : "--",
    },
    {
      id: "actions",
      header: "Actions",
      size: 80,
      meta: { cellClassName: tableCellClass },
      cell: ({ row: { original } }) => (
        <UserActionsMenu
          user={original}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ];
}

function UsersTable<TData>({
  table,
  isLoading,
  emptyMessage = "No users",
}: {
  table: TableType<TData>;
  isLoading: boolean;
  emptyMessage?: string;
}) {
  const columnCount = table.getAllLeafColumns().length;
  const pageSize = table.getState().pagination.pageSize;

  return (
    <Table className="min-w-[720px] md:min-w-0 md:table-fixed">
      <TableHeader className="bg-[#FAFAFA] dark:border-border dark:bg-muted/40 [&_tr]:border-b [&_tr]:border-[#E9EAEB]">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id} className="hover:bg-transparent">
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                colSpan={header.colSpan}
                style={{
                  width: `${(header.getSize() / table.getTotalSize()) * 100}%`,
                }}
                className={cn(
                  tableHeadClass,
                  header.column.columnDef.meta?.headerClassName,
                )}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          [...Array(pageSize)].map((_, index) => (
            <TableRow
              key={index}
              className="border-b border-[#E9EAEB] last:border-b-0 hover:bg-transparent dark:border-border"
            >
              <TableCell colSpan={columnCount} className="px-4 py-4 md:px-6">
                <Skeleton className="h-10 w-full" />
              </TableCell>
            </TableRow>
          ))
        ) : table.getRowModel().rows.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell
              colSpan={columnCount}
              className="px-4 py-16 text-center text-muted-foreground md:px-6"
            >
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="border-b border-[#E9EAEB] last:border-b-0 hover:bg-transparent dark:border-border"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  style={{
                    width: `${(cell.column.getSize() / table.getTotalSize()) * 100}%`,
                  }}
                  className={
                    cell.column.columnDef.meta?.cellClassName ?? tableCellClass
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function RouteComponent() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();

  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );

  const [userToEdit, setUserToEdit] = React.useState<User | null>(null);
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null);

  const users = useQuery(
    usersListQueryOptions({ ...searchParams, search: searchQuery }),
  );

  const columns = React.useMemo(
    () =>
      buildColumns(
        (user) => navigate({ to: "/users/$id", params: { id: user.id } }),
        setUserToEdit,
        setUserToDelete,
      ),
    [navigate],
  );

  const table = useReactTable({
    data: users.data?.items ?? [],
    columns,
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
    },
    rowCount: users.data?.count ?? 0,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const totalCount = users.data?.count ?? 0;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl leading-8 font-semibold text-[#181D27] dark:text-foreground">
          Users
        </h1>

        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 md:w-auto md:shrink-0">
          <div className="relative min-w-0 flex-1 md:w-72">
            <Search className="absolute top-1/2 left-3 size-5 -translate-y-1/2 text-[#717680] dark:text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={rawSearchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-10 py-0 pl-10 text-sm shadow-xs"
            />
          </div>

          <UsersFilterPopover
            isActive={searchParams.is_active}
            onApply={(isActive) =>
              navigate({
                to: ".",
                search: (s) => ({ ...s, page: 1, is_active: isActive }),
              })
            }
          />

          <CreateDialog asChild>
            <Button className="h-10 shrink-0 bg-[#DD2590] hover:bg-[#C4207D] dark:bg-[#DD2590] dark:hover:bg-[#C4207D]">
              <PlusIcon />
              New User
            </Button>
          </CreateDialog>
        </div>
      </div>

      <div className="overflow-hidden rounded-[10px] border border-[#E9EAEB] bg-card shadow-xs dark:border-border">
        {!users.isSuccess && !users.isLoading ? (
          <div className="px-4 py-16 text-center text-muted-foreground md:px-6">
            Error loading users
          </div>
        ) : (
          <UsersTable table={table} isLoading={users.isLoading} />
        )}

        <MeetingsPagination table={table} totalCount={totalCount} />
      </div>

      <EditDialog
        user={userToEdit}
        open={userToEdit !== null}
        onOpenChange={(open) => {
          if (!open) setUserToEdit(null);
        }}
      />

      <DeleteUserDialog
        user={userToDelete}
        open={userToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setUserToDelete(null);
        }}
      />
    </div>
  );
}

function DeleteUserDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn(id: string) {
      return api.auth.raw.delete(`api/v1/users/${id}`);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      void invalidateBillingSeatUsage(queryClient);
      if (user) {
        queryClient.removeQueries({ queryKey: ["users", "detail", user.id] });
      }
      onOpenChange(false);
      toast.success(`User "${user?.full_name ?? ""}" deleted successfully`);
    },
    async onError(error) {
      if (error instanceof HTTPError) {
        try {
          const body = await error.response.json();
          if (body.detail) {
            toast.error(body.detail);
            return;
          }
        } catch {
          // ignore json parse errors
        }
      }
      toast.error("Failed to delete user");
    },
  });

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Delete Team Member"
      description={
        user
          ? `Are you sure you want to delete the team member "${user.full_name}"? This action cannot be undone.`
          : ""
      }
      onConfirm={() => {
        if (user) mutation.mutate(user.id);
      }}
      confirmText="Delete"
      cancelText="Cancel"
      variant="destructive"
      isLoading={mutation.isPending}
      icon={ConfirmationDialogWarningIcon}
      backgroundDecoration
      emphasizeCancel
    />
  );
}

function EditDialog({
  user,
  open,
  onOpenChange,
}: {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();

  const [unsavedDialogOpen, setUnsavedDialogOpen] = React.useState(false);

  const form = useForm<UserUpdateFormData>({
    resolver: zodResolver(userUpdateFormSchema),
    values: {
      full_name: user?.full_name ?? "",
      email: user?.email ?? "",
      password: undefined,
      is_active: user?.is_active ?? true,
    },
  });

  const isDirty = form.formState.isDirty;

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UserUpdateFormData;
    }) => {
      const response = await api.auth.raw
        .put(`api/v1/users/${id}`, { json: data })
        .json<User>();
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.setQueryData(["users", "detail", data.id], data);

      form.reset();
      setUnsavedDialogOpen(false);
      onOpenChange(false);
      toast.success(`User "${data.full_name}" updated successfully`);
    },
    onError(error) {
      if (error instanceof HTTPError) {
        const body: unknown = error.data;
        const passwordError = getBackendFieldError(body, "password");

        if (error.response.status === 422 && passwordError) {
          form.setError(
            "password",
            { type: "server", message: passwordError },
            { shouldFocus: true },
          );
          return;
        }

        const detailMessage = getBackendDetailMessage(body);
        if (
          error.response.status === 400 &&
          detailMessage === DUPLICATE_EMAIL_ERROR
        ) {
          form.setError(
            "email",
            { type: "server", message: detailMessage },
            { shouldFocus: true },
          );
          return;
        }

        if (detailMessage) {
          toast.error(detailMessage);
          return;
        }
      }
      toast.error("Failed to update user");
    },
  });

  const handleRequestClose = () => {
    if (isDirty) {
      setUnsavedDialogOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const handleKeepEditing = () => {
    setUnsavedDialogOpen(false);
  };

  const handleDiscardAndLeave = () => {
    form.reset();
    setUnsavedDialogOpen(false);
    onOpenChange(false);
  };

  const handleSaveAndLeave = () => {
    form.handleSubmit(
      (data) => {
        if (user) mutation.mutate({ id: user.id, data });
      },
      () => {
        // Validation failed: return to the form so the errors are visible.
        setUnsavedDialogOpen(false);
      },
    )();
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) {
            onOpenChange(true);
          } else {
            handleRequestClose();
          }
        }}
      >
        <DialogContent className="max-w-[calc(100%-1rem)]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            {user && (
              <UserUpdateForm
                form={form}
                disabled={mutation.isPending}
                buttonText={mutation.isPending ? "Saving..." : "Save Changes"}
                onSubmit={(data) => mutation.mutate({ id: user.id, data })}
                onCancel={handleRequestClose}
              />
            )}
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={unsavedDialogOpen}
        onOpenChange={(next) => {
          if (!next) handleKeepEditing();
        }}
        onKeepEditing={handleKeepEditing}
        onDiscardAndLeave={handleDiscardAndLeave}
        onSaveAndLeave={handleSaveAndLeave}
        isSaving={mutation.isPending}
      />
    </>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();
  const form = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: generateDefaultValue(userFormSchema),
    mode: "onChange",
  });
  const password = form.watch("password");
  const isPasswordValid = passwordSchema.safeParse(password).success;

  React.useEffect(() => {
    if (isPasswordValid) {
      void form.trigger(["full_name", "email"]);
    }
  }, [form, isPasswordValid]);

  const mutation = useMutation({
    mutationFn(data: UserFormData) {
      return api.auth.raw.post("api/v1/users/", { json: data }).json<User>();
    },
    onSuccess(data) {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      void invalidateBillingSeatUsage(queryClient);
      queryClient.setQueryData(["users", "detail", data.id], data);

      form.reset();
      setOpen(false);
      toast.success(`User "${data.full_name}" created successfully`);
    },
    async onError(error, variables) {
      const billingError = await asBillingError(error);
      if (billingError?.code === "SEAT_LIMIT_REACHED") {
        toast.error(
          `User was not created. ${billingError.seats_used ?? "All"} of ${billingError.seat_limit ?? "the available"} seats are in use; 1 additional seat was requested.`,
          {
            action: {
              label: "Billing",
              onClick: () => {
                window.location.href =
                  billingError.upgrade_url ?? "/settings/billing";
              },
            },
          },
        );
        return;
      }
      if (billingError?.code === "PAYMENT_SERVICE_UNAVAILABLE") {
        toast.error(
          "Billing is temporarily unavailable. Please try again in a moment.",
          {
            action: {
              label: "Retry",
              onClick: () => mutation.mutate(variables),
            },
          },
        );
        return;
      }
      if (error instanceof HTTPError) {
        const body: unknown = error.data;
        const backendPasswordError = getBackendFieldError(body, "password");

        if (error.response.status === 422 && backendPasswordError) {
          form.setError(
            "password",
            { type: "server", message: backendPasswordError },
            { shouldFocus: true },
          );
          return;
        }

        const detailMessage = getBackendDetailMessage(body);
        if (
          error.response.status === 400 &&
          detailMessage === DUPLICATE_EMAIL_ERROR
        ) {
          form.setError(
            "email",
            { type: "server", message: detailMessage },
            { shouldFocus: true },
          );
          return;
        }

        if (detailMessage) {
          toast.error(detailMessage);
          return;
        }
      }
      toast.error("Failed to create user");
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent className="max-w-[calc(100%-1rem)]">
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
          <UserCreateForm
            form={form}
            disabled={mutation.isPending}
            buttonText={mutation.isPending ? "Creating..." : "Create User"}
            onSubmit={mutation.mutate}
            onCancel={() => handleOpenChange(false)}
          />
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

function UserCreateForm({
  form,
  disabled,
  buttonText,
  onSubmit,
  onCancel,
}: {
  form: UseFormReturn<UserFormData>;
  disabled?: boolean;
  buttonText: React.ReactNode;
  onSubmit: (data: UserFormData) => void;
  onCancel?: () => void;
}) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="mt-4">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <PasswordInput placeholder="Enter password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable or disable user account
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="dark:text-white dark:hover:text-white"
            >
              Cancel
            </Button>
          )}

          <Button
            type="submit"
            disabled={disabled || !form.formState.isValid}
            className="dark:bg-[#DD2590] dark:hover:bg-[#C4207D]"
          >
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function UserUpdateForm({
  form,
  disabled,
  buttonText,
  onSubmit,
  onCancel,
}: {
  form: UseFormReturn<UserUpdateFormData>;
  disabled?: boolean;
  buttonText: React.ReactNode;
  onSubmit: (data: UserUpdateFormData) => void;
  onCancel?: () => void;
}) {
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="full_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter full name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password (leave empty to keep current)</FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder="Enter new password"
                  {...field}
                  onChange={(e) =>
                    e.target.value === ""
                      ? field.onChange(undefined) // treat empty string as unset
                      : field.onChange(e)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Active Status</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable or disable user account
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="bg-[#DD2590]"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="dark:text-white dark:hover:text-white"
            >
              Cancel
            </Button>
          )}

          <Button type="submit" disabled={disabled || !form.formState.isDirty}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}
